import { useState } from 'react';
import { api, type OwnLifeEvent } from '../lib/api.js';
import { Link } from 'react-router-dom';

/**
 * The big ongoing things, wherever a day is shown.
 *
 * WHAT THIS REPLACED, AND WHY. The first version was a red panel on Today with
 * a permanent "+ Something big going on" button opening a one-field form. Its
 * owner's verdict was "this is yet another thing to fill out", and he was right
 * twice over: it was a separate authoring chore bolted onto the screen he opens
 * most — a standing invitation to go and formally register the worst thing in
 * his year — and it duplicated, because the thing had usually just been said,
 * out loud, in an entry. The app made him type it again.
 *
 * So there is no card, no button, no colour, and nothing on Today at all. This
 * lives at the foot of a day's fold, which is zero pixels until the fold is
 * opened. That is the trade the whole design makes: worse discoverability,
 * bought with never once presenting itself unasked.
 *
 * THE ONE RULE. Marking something is two taps and no keyboard. Naming is a
 * separate step that may never be taken, and an unnamed event works everywhere:
 * the daily read is told there is something and told not to guess what, and a
 * share link says "Something big · Since 4 August". Naming is the hard
 * part, and it is hardest in exactly the week this matters — nobody in the
 * first days of something has a phrase for it, and demanding one is asking
 * someone to summarise their own catastrophe before the app will agree to know
 * about it.
 *
 * NO EXAMPLE TEXT. Not here, not ever, in any field this file renders. Every
 * candidate example is a bereavement, a diagnosis or a separation, and ghost
 * text would name someone's worst outcome before they did. This app shipped
 * with a real one hardcoded once already.
 */

/** Local midday: a bare YYYY-MM-DD parses as UTC and prints yesterday west of Greenwich. */
function formatDay(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
  });
}

export function todayLocal(): string {
  return new Date().toLocaleDateString('en-CA');
}



/**
 * The date line, for every surface that shows one.
 *
 * NO VERB. It used to read "Going on since 4 August" / "Was going on 4 August
 * to 12 September", and the verb was the whole problem: "going on" is a claim
 * about somebody's life, and this feature holds a new job and a new baby as
 * readily as a bereavement. A date needs no verb to be legible — tense is
 * carried by the shape of the line, so nothing can go stale either. The comment
 * that used to sit here guarded against a closed event still saying "going on";
 * nothing says it now.
 *
 * "From X to Y" and not the bare "X to Y" the page already printed. Under a
 * label people actually use — a name, "the move", "Mum" — two bare dates under a
 * name is a headstone. One word blocks that reading.
 *
 * Exported because four surfaces print this and they used to print three
 * different versions of it: the fold said "Going on since", LifeEventPage said
 * "Since", and LifeEventPage had no one-day branch at all, so a wedding marked
 * and closed the same day read "12 June to 12 June".
 */
export function whenLine(startedOn: string, endedOn: string | null): string {
  if (!endedOn) return `Since ${formatDay(startedOn)}`;
  if (endedOn === startedOn) return `On ${formatDay(startedOn)}`;
  return `From ${formatDay(startedOn)} to ${formatDay(endedOn)}`;
}


/**
 * What an event with neither a name nor a quote says.
 *
 * NOT "bigger". A comparative needs something to compare against, and the only
 * baseline on screen is this man's ordinary bad days — so the app-authored
 * default parsed as "worse than your usual trouble", which files a new baby as
 * a deterioration on a link a GP opens cold. "Big" is a size with nothing to
 * measure against, which is the only claim two taps ever entitled the app to
 * make. Note the app is right to keep "bigger" at JournalEntry.tsx:381 — "Is
 * this bigger than today?" states its own comparand.
 *
 * Two words also fixes two things the length was breaking: the close
 * confirmation interpolates this lowercased in front of " on {date}" and so
 * printed "Close off something bigger going on on 7 September", and the Trends
 * band caption is a chart label with room for a name, not a sentence.
 *
 * One string, used everywhere, IMPORTED AND NEVER RETYPED. Three near-identical
 * literals were live — Share.tsx promised "something big going on" while this
 * said "Something bigger going on" — and that drift is most of why the line read
 * as filler. It no longer matches the button that creates it, on purpose: the
 * button is a control and says what pressing it does; this is a noun standing in
 * for a name that was never given.
 */
export const FALLBACK = 'Something big';



/** Covering a given day: started on or before it, and not yet ended by it. */
export function covering(events: OwnLifeEvent[], day: string): OwnLifeEvent[] {
  return events.filter((e) => e.startedOn <= day && (e.endedOn === null || e.endedOn >= day));
}

/**
 * A stated fact that is also the control.
 *
 * Not a date picker sitting open: on the day something happens, the date is
 * almost always right, and an input demanding attention is one more thing to
 * deal with. It reads as a sentence and turns into a picker on request.
 *
 * This exists because the alternative — "open the day it started in the Log" —
 * has a hole in it. History builds its list purely from days that have entries,
 * so a day with nothing logged renders no section and no fold, and the day
 * someone goes into hospital is very often a day with nothing logged.
 */
function DateLine({
  day,
  onChange,
  prefix,
}: {
  day: string;
  onChange: (d: string) => void;
  prefix: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input
        className="input"
        type="date"
        autoFocus
        value={day}
        max={todayLocal()}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        onBlur={() => setEditing(false)}
      />
    );
  }
  return (
    <p className="faint" style={{ margin: 0 }}>
      {prefix} {day === todayLocal() ? 'today' : formatDay(day)}{' '}
      <button className="linkish" onClick={() => setEditing(true)}>
        change
      </button>
    </p>
  );
}

/**
 * The line above a day's figures, when something was going on.
 *
 * At the top rather than the foot because it is the frame the figures are read
 * through, not another figure. Tapping it opens the same editor as everywhere
 * else — the label is the one string that ever leaves this app for a partner or
 * a clinician, and people rarely get it right first time from inside the week
 * it started.
 *
 * There is no counter. "day 12" turns a bereavement into a tally and "day 214"
 * in November is the exact sentence that makes someone close the app; "day 12
 * of 47" is worse still, because it puts a boundary around a grief in
 * retrospect. `daysRunning` still goes out on a share link, where someone
 * reading a duration is a different act from being handed a running total of
 * your own.
 */
/** Naming, renaming, or removing one. Never a required step. */
export function NameEditor({
  event,
  onDone,
  onCancel,
  hasEntry,
  /*
   * A starting point for the field — in practice the span quoted back from the
   * entry that raised the offer.
   *
   * Pre-filling a field is normally how a model's phrasing becomes somebody's
   * own words without being their own words, which is why nothing here is ever
   * pre-filled from a summary. This is the exception that proves the rule: the
   * span is verified character-for-character against the transcript on the
   * server, so accepting it unchanged stores a sentence they actually wrote.
   */
  suggested,
}: {
  event: OwnLifeEvent;
  /** Receives the saved row, so a caller showing the label can update it. */
  onDone: (updated?: OwnLifeEvent) => void;
  onCancel: () => void;
  hasEntry?: boolean;
  suggested?: string | null;
}): JSX.Element {
  const [label, setLabel] = useState(event.label || suggested || '');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (confirmDelete) {
    return (
      <div className="life-event-edit stack-sm stack">
        {/* Says "it", never the label. The shipped delete fired an OS dialog
            quoting the person's own sentence about the worst thing in their
            year back at them in a grey system box. The share clause is the part
            that actually changes the decision, since the label is the only
            thing here that ever reached another person. */}
        <p style={{ margin: 0 }}>
          Delete it? It goes from every day it was on, and from anything you've shared. This can't be
          undone.
        </p>
        <div className="row">
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void api.deleteLifeEvent(event.id).then(() => onDone()).catch(() => setBusy(false));
            }}
          >
            Delete it
          </button>
          <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>
            Keep it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="life-event-edit stack-sm stack">
      {/* Their own sentence, offered rather than imposed.
          A field that arrives pre-filled is accepted unchanged most of the
          time, so nothing here is ever pre-filled from a summary — but this is
          not a summary, it is what they wrote, checked character for character
          on the server. Offering it as one tap keeps the choice theirs and
          still spares them composing a phrase for the worst week of their year. */}
      {event.quote && !event.label && (
        <div className="stack-sm stack">
          <span className="faint">What you said that day</span>
          <p className="life-event-words life-event-words-quoted" style={{ margin: 0 }}>
            {'\u201c'}
            {event.quote}
            {'\u201d'}
          </p>
          <button className="linkish" onClick={() => setLabel(event.quote ?? '')}>
            Use these words
          </button>
        </div>
      )}

      <label className="field">
        <span>A name for it, if you want one</span>
        {/* No placeholder. See the note at the top of this file. */}
        {/* Focus only when there is nothing to read.
            Arriving pre-filled, autoFocus did two unhelpful things at once: it
            threw the keyboard up on a step whose whole point is that typing is
            optional, and it parked the cursor at the end, so a phone showed the
            TAIL of the sentence — "t Toby, they reckon maybe a month". Empty,
            focus is a convenience; pre-filled, it is an instruction to edit. */}
        <input
          className="input"
          autoFocus={label === ''}
          value={label}
          maxLength={200}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>
      {/* Points at their own sentences rather than at a blank box — the honest
          form of the pre-filled field, with no model in it. Conditional, or it
          points at a day where nothing was written. */}
      {/*
        A link, not a guess. This said "The words are probably in what you wrote
        that day" while `voiceLogId` sat on the event holding the id of the exact
        entry that raised it — the app hedging about something it knows. If the
        id is missing the sentence still has to work, so the guess survives as
        the fallback rather than as the answer.
      */}
      {hasEntry && !event.quote && (
        event.voiceLogId ? (
          <Link className="linkish" to={`/journal/${event.voiceLogId}`}>
            Read what you wrote that day
          </Link>
        ) : (
          <p className="faint" style={{ margin: 0 }}>
            The words are probably in what you wrote that day.
          </p>
        )
      )}
      {/* Said once, here, rather than left to be discovered on the share
          screen — where the moment somebody is generating a link for a
          clinician is the worst possible moment to feel they ought to produce a
          tidy phrase for their bereavement. */}
      <p className="faint" style={{ margin: 0 }}>
        A name is the only part of this a shared link can carry.
      </p>
      <DateLine
        day={event.startedOn}
        prefix="Started"
        onChange={(d) => {
          void api.updateLifeEvent(event.id, { startedOn: d }).then(onDone);
        }}
      />
      <div className="row">
        <button
          className="btn btn-secondary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void api
              .updateLifeEvent(event.id, { label: label.trim() })
              .then((updated) => onDone(updated))
              .catch(() => setBusy(false));
          }}
        >
          Save it
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {/* Ending and deleting are different acts and must not sit at the same
          weight. Ending keeps the weeks it covered; deleting is for the one you
          typed wrong or never meant. */}
      <button className="linkish linkish-quiet" onClick={() => setConfirmDelete(true)}>
        Delete this
      </button>
    </div>
  );
}

/**
 * The control at the foot of a day's fold.
 *
 * Exactly one at a time: mark something, or close off the thing that is
 * running. Ghost weight, matching "Delete this entry" — this is the only
 * always-available authoring control in the app and it is deliberately the
 * quietest thing on the screen.
 */


